import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Users, Plus, X, UserPlus, Upload, Download, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import {
  getAllActiveUsers,
  bulkAssignUsersToOrganization,
  createUserInOrganization,
  ActiveUser,
} from '@/services/PagesServices/OrganizationService';
import {
  uploadUsers as bulkUploadUsers,
  downloadTemplate,
  downloadSample,
} from '@/services/PagesServices/SuperAdminBulkUploadService';
import { LoadingSpinner } from '@/Components/common';
import UploadDropzone from '@pages/SuperAdmin/BulkUploader/components/UploadDropzone';
import StatsCards from '@pages/SuperAdmin/BulkUploader/components/StatsCards';
import ValidationErrors from '@pages/SuperAdmin/BulkUploader/components/ValidationErrors';
import DataPreview from '@pages/SuperAdmin/BulkUploader/components/DataPreview';
import './AddUsersToOrganization.css';

type TabKey = 'existing' | 'create' | 'bulk';

type CreateFormState = {
  firstName: string;
  lastName: string;
  email: string;
  role: 'learner' | 'instructor' | 'admin';
  managerEmail: string;
  jobTitle: string;
  department: string;
  location: string;
  phone: string;
};

const EMPTY_CREATE_FORM: CreateFormState = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'learner',
  managerEmail: '',
  jobTitle: '',
  department: '',
  location: '',
  phone: '',
};

interface BulkValidationError {
  row: number;
  field: string;
  error: string;
}

interface BulkPreviewData {
  [key: string]: string;
}

interface BulkUploadStats {
  totalRows: number;
  valid: number;
  errors: number;
  warnings: number;
  duplicates: number;
}

const AddUsersToOrganization: React.FC = () => {
  const { id, name } = useParams<{ id: string; name: string }>();
  const navigate = useNavigate();
  const organizationName = decodeURIComponent(name || 'Organization');
  const organizationIdNum = id ? parseInt(id, 10) : NaN;

  const [activeTab, setActiveTab] = useState<TabKey>('existing');

  // ── EXISTING USERS TAB STATE — unchanged from the previous version ──
  const [allUsers, setAllUsers] = useState<ActiveUser[]>([]);
  const [displayedUsers, setDisplayedUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const USERS_PER_PAGE = 30;

  // ── CREATE NEW USER TAB STATE ──
  const [createForm, setCreateForm] = useState<CreateFormState>(EMPTY_CREATE_FORM);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string>>({});

  // ── BULK UPLOAD TAB STATE ──
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkStats, setBulkStats] = useState<BulkUploadStats | null>(null);
  const [bulkValidationErrors, setBulkValidationErrors] = useState<BulkValidationError[]>([]);
  const [bulkPreviewData, setBulkPreviewData] = useState<BulkPreviewData[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const result = await getAllActiveUsers();

      if (result.success && result.data) {
        setAllUsers(result.data);
        setDisplayedUsers(result.data.slice(0, USERS_PER_PAGE));
        setHasMore(result.data.length > USERS_PER_PAGE);
        setCurrentPage(1);
      } else {
        toast.error(result.error?.message || 'Failed to load users');
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreUsers = () => {
    setLoadingMore(true);

    setTimeout(() => {
      const nextPage = currentPage + 1;
      const startIndex = currentPage * USERS_PER_PAGE;
      const endIndex = startIndex + USERS_PER_PAGE;

      const filteredData = searchTerm
        ? allUsers.filter((user) => {
            const search = searchTerm.toLowerCase();
            const firstName = (user.firstName || '').toLowerCase();
            const lastName = (user.lastName || '').toLowerCase();
            const fullName = `${firstName} ${lastName}`.trim();
            const email = (user.email || '').toLowerCase();
            const role = (user.role || '').toLowerCase();
            return fullName.includes(search) || email.includes(search) || role.includes(search);
          })
        : allUsers;

      const moreUsers = filteredData.slice(startIndex, endIndex);
      setDisplayedUsers((prev) => [...prev, ...moreUsers]);
      setCurrentPage(nextPage);
      setHasMore(endIndex < filteredData.length);
      setLoadingMore(false);
    }, 300);
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = allUsers.filter((user) => {
        const search = searchTerm.toLowerCase();
        const firstName = (user.firstName || '').toLowerCase();
        const lastName = (user.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        const email = (user.email || '').toLowerCase();
        const role = (user.role || '').toLowerCase();
        return fullName.includes(search) || email.includes(search) || role.includes(search);
      });
      setDisplayedUsers(filtered.slice(0, USERS_PER_PAGE));
      setCurrentPage(1);
      setHasMore(filtered.length > USERS_PER_PAGE);
    } else {
      setDisplayedUsers(allUsers.slice(0, USERS_PER_PAGE));
      setCurrentPage(1);
      setHasMore(allUsers.length > USERS_PER_PAGE);
    }
  }, [searchTerm, allUsers]);

  const filteredUsers = displayedUsers;

  const handleToggleUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter((sid) => sid !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const handleSelectAll = () => {
    const allFilteredUsers = searchTerm
      ? allUsers.filter((user) => {
          const search = searchTerm.toLowerCase();
          const firstName = (user.firstName || '').toLowerCase();
          const lastName = (user.lastName || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim();
          const email = (user.email || '').toLowerCase();
          const role = (user.role || '').toLowerCase();
          return fullName.includes(search) || email.includes(search) || role.includes(search);
        })
      : allUsers;

    if (selectedUserIds.length === allFilteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(allFilteredUsers.map((u) => u.uuid));
    }
  };

  const handleAddUsers = async () => {
    if (selectedUserIds.length === 0 || !id) return;

    setIsProcessing(true);

    try {
      const organizationId = parseInt(id);
      const result = await bulkAssignUsersToOrganization(selectedUserIds, organizationId);

      if (result.success && result.data) {
        const { successCount, failedCount, skippedCount } = result.data;

        if (successCount > 0 && failedCount === 0 && skippedCount === 0) {
          toast.success(
            `${successCount} user${successCount > 1 ? 's' : ''} added to organization successfully`
          );
        } else if (successCount > 0) {
          const messages: string[] = [];
          if (successCount > 0) messages.push(`${successCount} added`);
          if (skippedCount > 0) messages.push(`${skippedCount} skipped`);
          if (failedCount > 0) messages.push(`${failedCount} failed`);

          toast.info(messages.join(', '));
        } else {
          toast.error('Failed to add users to organization');
        }

        navigate(`/superadmin/organizations/${id}`);
      } else {
        toast.error(result.error?.message || 'Failed to add users to organization');
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error('Error adding users:', error);
      toast.error('Failed to add users to organization');
      setIsProcessing(false);
    }
  };

  const allFilteredUsers = searchTerm
    ? allUsers.filter((user) => {
        const search = searchTerm.toLowerCase();
        const firstName = (user.firstName || '').toLowerCase();
        const lastName = (user.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        const email = (user.email || '').toLowerCase();
        const role = (user.role || '').toLowerCase();
        return fullName.includes(search) || email.includes(search) || role.includes(search);
      })
    : allUsers;

  const isAllSelected = allFilteredUsers.length > 0 && selectedUserIds.length === allFilteredUsers.length;

  // ── CREATE NEW USER HANDLERS ──
  const updateCreateField = <K extends keyof CreateFormState>(field: K, value: CreateFormState[K]) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
    if (createFieldErrors[field as string]) {
      setCreateFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Number.isNaN(organizationIdNum)) {
      toast.error('Invalid organization');
      return;
    }

    setCreateFieldErrors({});
    setCreateSubmitting(true);
    try {
      const payload = {
        firstName: createForm.firstName.trim(),
        lastName: createForm.lastName.trim(),
        email: createForm.email.trim(),
        role: createForm.role,
        managerEmail: createForm.managerEmail.trim() || undefined,
        jobTitle: createForm.jobTitle.trim() || undefined,
        department: createForm.department.trim() || undefined,
        location: createForm.location.trim() || undefined,
        phone: createForm.phone.trim() || undefined,
      };

      const result = await createUserInOrganization(organizationIdNum, payload);
      if (result.success && result.data) {
        const note = result.data.invitationSent
          ? ''
          : ' (invitation email could not be delivered)';
        toast.success(`${createForm.firstName} ${createForm.lastName} created and added to ${organizationName}${note}`);
        // Reset form so the SuperAdmin can immediately add another user.
        setCreateForm(EMPTY_CREATE_FORM);
        // Refresh the existing-users list so the just-created user shows up
        // there too if the SuperAdmin switches tabs.
        fetchUsers();
      } else {
        // Map field-level errors back onto the form
        if (result.errors && result.errors.length > 0) {
          const fieldErrors: Record<string, string> = {};
          for (const err of result.errors) {
            fieldErrors[err.field] = err.message;
          }
          setCreateFieldErrors(fieldErrors);
          toast.error(result.message || 'Validation failed');
        } else {
          toast.error(result.error?.message || result.message || 'Failed to create user');
        }
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error?.message || 'Failed to create user');
    } finally {
      setCreateSubmitting(false);
    }
  };

  // ── BULK UPLOAD HANDLERS ──
  const resetBulkState = () => {
    setBulkFile(null);
    setBulkStats(null);
    setBulkValidationErrors([]);
    setBulkPreviewData([]);
    setBulkError(null);
  };

  const handleBulkFileSelect = (selectedFile: File) => {
    setBulkFile(selectedFile);
    setBulkError(null);

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as BulkPreviewData[];

        if (data.length === 0) {
          setBulkError('CSV file is empty');
          return;
        }

        setBulkStats({
          totalRows: data.length,
          valid: data.length,
          errors: 0,
          warnings: 0,
          duplicates: 0,
        });
        setBulkValidationErrors([]);
        setBulkPreviewData(data);
      },
      error: (err) => {
        setBulkError(`Failed to parse CSV: ${err.message}`);
        setBulkStats(null);
        setBulkValidationErrors([]);
        setBulkPreviewData([]);
      },
    });
  };

  const handleBulkConfirm = async () => {
    if (!bulkFile || Number.isNaN(organizationIdNum)) return;

    setBulkUploading(true);
    setBulkError(null);
    try {
      const result = await bulkUploadUsers(bulkFile, String(organizationIdNum));

      setBulkStats({
        totalRows: result.data.totalRows,
        valid: result.data.validRows,
        errors: result.data.errorRows,
        warnings: (result.data.warnings || []).length,
        duplicates: 0,
      });
      setBulkValidationErrors(
        (result.data.errors || []).map((err: any) => ({
          row: err.row,
          field: err.field,
          error: err.message,
        }))
      );

      if (result.success) {
        toast.success(
          `Upload completed: ${result.data.inserted ?? result.data.validRows ?? 0} user(s) created and added to ${organizationName}`
        );
        resetBulkState();
      } else {
        toast.error(result.message || 'Bulk upload failed');
      }
    } catch (error: any) {
      console.error('Bulk upload error:', error);
      setBulkError(error?.message || 'Bulk upload failed');
      toast.error(error?.message || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate('users');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to download template');
    }
  };

  const handleDownloadSample = async () => {
    try {
      await downloadSample('users');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to download sample');
    }
  };

  return (
    <div className="add-users-page">
      {/* Header */}
      <div className="add-users-header">
        <button
          className="add-users-back-btn"
          onClick={() => navigate(`/superadmin/organizations/${id}`)}
          disabled={isProcessing || createSubmitting || bulkUploading}
        >
          <ArrowLeft size={20} />
          Back to {organizationName}
        </button>

        <div className="add-users-header-content">
          <h1>Add Users to Organization</h1>
          <p>Pick existing users, create a new one, or bulk-upload via CSV — all are added to "{organizationName}"</p>
        </div>
      </div>

      {/* Tab strip */}
      <div className="add-users-tabs" role="tablist" aria-label="Add Users methods">
        <button
          role="tab"
          aria-selected={activeTab === 'existing'}
          className={`add-users-tab ${activeTab === 'existing' ? 'active' : ''}`}
          onClick={() => setActiveTab('existing')}
          disabled={isProcessing || createSubmitting || bulkUploading}
        >
          <Users size={16} /> Existing Users
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'create'}
          className={`add-users-tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
          disabled={isProcessing || createSubmitting || bulkUploading}
        >
          <UserPlus size={16} /> Create New User
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'bulk'}
          className={`add-users-tab ${activeTab === 'bulk' ? 'active' : ''}`}
          onClick={() => setActiveTab('bulk')}
          disabled={isProcessing || createSubmitting || bulkUploading}
        >
          <Upload size={16} /> Bulk Upload
        </button>
      </div>

      {/* ── EXISTING USERS TAB ── */}
      {activeTab === 'existing' && (
        <>
          <div className="add-users-controls">
            <div className="add-users-search-container">
              <Search className="add-users-search-icon" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="add-users-search-input"
                disabled={isProcessing}
              />
            </div>

            {allFilteredUsers.length > 0 && (
              <button
                className="add-users-select-all-btn"
                onClick={handleSelectAll}
                disabled={isProcessing}
              >
                {isAllSelected ? 'Deselect All' : 'Select All'} ({allFilteredUsers.length})
              </button>
            )}
          </div>

          <div className="add-users-main-content">
            {/* Left Column - Users List */}
            <div className="add-users-list-section">
              {loading ? (
                <div className="add-users-loading">
                  <LoadingSpinner size="lg" />
                  <p>Loading users...</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="add-users-empty">
                  <Users size={64} color="#94a3b8" />
                  <h3>{searchTerm ? 'No users found' : 'No users available'}</h3>
                  <p>
                    {searchTerm
                      ? `No users match your search "${searchTerm}"`
                      : 'There are no active users available to add to this organization'}
                  </p>
                </div>
              ) : (
                <div className="add-users-table-container">
                  <table className="add-users-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                            disabled={isProcessing}
                            className="add-users-checkbox"
                          />
                        </th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr
                          key={user.uuid}
                          className={`add-users-row ${selectedUserIds.includes(user.uuid) ? 'selected' : ''}`}
                          onClick={() => !isProcessing && handleToggleUser(user.uuid)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user.uuid)}
                              onChange={() => handleToggleUser(user.uuid)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={isProcessing}
                              className="add-users-checkbox"
                            />
                          </td>
                          <td>
                            <div className="add-users-name-cell">
                              <div className="add-users-avatar">
                                {user.firstName?.[0] || user.email[0].toUpperCase()}
                              </div>
                              <span className="add-users-name">
                                {user.firstName && user.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : user.email}
                              </span>
                            </div>
                          </td>
                          <td className="add-users-email">{user.email}</td>
                          <td>
                            <span className={`add-users-role-badge role-${user.role.toLowerCase()}`}>
                              {user.role}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {hasMore && !searchTerm && (
                    <div className="add-users-load-more-container">
                      <button
                        className="add-users-load-more-btn"
                        onClick={loadMoreUsers}
                        disabled={loadingMore || isProcessing}
                      >
                        {loadingMore ? (
                          <>
                            <LoadingSpinner size="sm" />
                            Loading more users...
                          </>
                        ) : (
                          `Load More (${allUsers.length - displayedUsers.length} remaining)`
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column - Selected Users Box */}
            <div className="add-users-selected-section">
              <div className="add-users-selected-box">
                <div className="add-users-selected-header">
                  <h3>
                    <Users size={20} />
                    Selected Users ({selectedUserIds.length})
                  </h3>
                  {selectedUserIds.length > 0 && (
                    <button
                      className="add-users-clear-all-btn"
                      onClick={() => setSelectedUserIds([])}
                      disabled={isProcessing}
                    >
                      <X size={16} />
                      Clear All
                    </button>
                  )}
                </div>

                <div className="add-users-selected-list">
                  {selectedUserIds.length === 0 ? (
                    <div className="add-users-selected-empty">
                      <Users size={48} color="#cbd5e1" />
                      <p>No users selected</p>
                      <span>Select users from the list to add them</span>
                    </div>
                  ) : (
                    <>
                      {allUsers
                        .filter((user) => selectedUserIds.includes(user.uuid))
                        .map((user) => (
                          <div key={user.uuid} className="add-users-selected-item">
                            <div className="add-users-selected-item-avatar">
                              {user.firstName?.[0] || user.email[0].toUpperCase()}
                            </div>
                            <div className="add-users-selected-item-info">
                              <span className="add-users-selected-item-name">
                                {user.firstName && user.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : user.email}
                              </span>
                              <span className="add-users-selected-item-email">{user.email}</span>
                            </div>
                            <button
                              className="add-users-selected-item-remove"
                              onClick={() => handleToggleUser(user.uuid)}
                              disabled={isProcessing}
                              title="Remove from selection"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                    </>
                  )}
                </div>

                {selectedUserIds.length > 0 && (
                  <div className="add-users-selected-footer">
                    <button
                      className="add-users-submit-btn"
                      onClick={handleAddUsers}
                      disabled={isProcessing || selectedUserIds.length === 0}
                    >
                      <Plus size={18} />
                      {isProcessing
                        ? `Adding ${selectedUserIds.length} user${selectedUserIds.length > 1 ? 's' : ''}...`
                        : `Add ${selectedUserIds.length} User${selectedUserIds.length > 1 ? 's' : ''} to Organization`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── CREATE NEW USER TAB ── */}
      {activeTab === 'create' && (
        <form className="add-users-create-form" onSubmit={handleCreateSubmit}>
          <div className="add-users-create-intro">
            <p>
              Create a brand-new user and add them to <strong>{organizationName}</strong> in one step.
              An invitation email with a temporary password is sent automatically.
            </p>
          </div>

          <div className="add-users-create-grid">
            <div className="add-users-create-field">
              <label>First Name <span className="add-users-create-required">*</span></label>
              <input
                type="text"
                value={createForm.firstName}
                onChange={(e) => updateCreateField('firstName', e.target.value)}
                disabled={createSubmitting}
                required
              />
              {createFieldErrors.firstName && (
                <span className="add-users-create-error">{createFieldErrors.firstName}</span>
              )}
            </div>

            <div className="add-users-create-field">
              <label>Last Name <span className="add-users-create-required">*</span></label>
              <input
                type="text"
                value={createForm.lastName}
                onChange={(e) => updateCreateField('lastName', e.target.value)}
                disabled={createSubmitting}
                required
              />
              {createFieldErrors.lastName && (
                <span className="add-users-create-error">{createFieldErrors.lastName}</span>
              )}
            </div>

            <div className="add-users-create-field">
              <label>Email <span className="add-users-create-required">*</span></label>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => updateCreateField('email', e.target.value)}
                disabled={createSubmitting}
                required
              />
              {createFieldErrors.email && (
                <span className="add-users-create-error">{createFieldErrors.email}</span>
              )}
            </div>

            <div className="add-users-create-field">
              <label>Role <span className="add-users-create-required">*</span></label>
              <select
                value={createForm.role}
                onChange={(e) => updateCreateField('role', e.target.value as CreateFormState['role'])}
                disabled={createSubmitting}
              >
                <option value="learner">Learner</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
              </select>
              {createFieldErrors.role && (
                <span className="add-users-create-error">{createFieldErrors.role}</span>
              )}
            </div>

            <div className="add-users-create-field add-users-create-field-wide">
              <label>Manager Email</label>
              <input
                type="email"
                value={createForm.managerEmail}
                onChange={(e) => updateCreateField('managerEmail', e.target.value)}
                placeholder="Must already exist in the platform"
                disabled={createSubmitting}
              />
              <span className="add-users-create-hint">
                Optional. If provided, this user will report to the manager and the manager must already exist on the platform.
              </span>
              {createFieldErrors.managerEmail && (
                <span className="add-users-create-error">{createFieldErrors.managerEmail}</span>
              )}
            </div>

            <div className="add-users-create-field">
              <label>Job Title</label>
              <input
                type="text"
                value={createForm.jobTitle}
                onChange={(e) => updateCreateField('jobTitle', e.target.value)}
                disabled={createSubmitting}
              />
            </div>

            <div className="add-users-create-field">
              <label>Department</label>
              <input
                type="text"
                value={createForm.department}
                onChange={(e) => updateCreateField('department', e.target.value)}
                disabled={createSubmitting}
              />
            </div>

            <div className="add-users-create-field">
              <label>Location</label>
              <input
                type="text"
                value={createForm.location}
                onChange={(e) => updateCreateField('location', e.target.value)}
                disabled={createSubmitting}
              />
            </div>

            <div className="add-users-create-field">
              <label>Phone</label>
              <input
                type="tel"
                value={createForm.phone}
                onChange={(e) => updateCreateField('phone', e.target.value)}
                disabled={createSubmitting}
              />
              {createFieldErrors.phone && (
                <span className="add-users-create-error">{createFieldErrors.phone}</span>
              )}
            </div>
          </div>

          <div className="add-users-create-actions">
            <button
              type="button"
              className="add-users-create-cancel"
              onClick={() => setCreateForm(EMPTY_CREATE_FORM)}
              disabled={createSubmitting}
            >
              Reset
            </button>
            <button
              type="submit"
              className="add-users-create-submit"
              disabled={createSubmitting}
            >
              <UserPlus size={18} />
              {createSubmitting ? 'Creating…' : `Create & Add to ${organizationName}`}
            </button>
          </div>
        </form>
      )}

      {/* ── BULK UPLOAD TAB ── */}
      {activeTab === 'bulk' && (
        <div className="add-users-bulk-section">
          <div className="add-users-bulk-intro">
            <p>
              Upload a CSV to create users in bulk and add them to <strong>{organizationName}</strong>.
              Required columns: <code>first_name</code>, <code>last_name</code>, <code>email</code>, <code>role</code>.
              Optional: <code>manager_email</code>, <code>job_title</code>, <code>department</code>, <code>location</code>, <code>phone</code>.
            </p>
            <p className="add-users-bulk-guard">
              <strong>Manager guard:</strong> if a row has <code>manager_email</code>, that manager must already exist on the platform OR appear in an earlier row of the same CSV — otherwise the row is rejected.
            </p>
            <div className="add-users-bulk-template-row">
              <button type="button" className="add-users-bulk-template-btn" onClick={handleDownloadTemplate}>
                <Download size={16} />
                Download Template
              </button>
              <button type="button" className="add-users-bulk-sample-btn" onClick={handleDownloadSample}>
                <Download size={16} />
                Download Sample
              </button>
            </div>
          </div>

          {bulkError && (
            <div className="add-users-bulk-error">
              <strong>Error:</strong> {bulkError}
            </div>
          )}

          {!bulkFile ? (
            <UploadDropzone onFileSelect={handleBulkFileSelect} />
          ) : (
            <>
              <div className="add-users-bulk-selected-file">
                <div className="add-users-bulk-selected-info">
                  <FileText size={20} />
                  <div>
                    <div className="add-users-bulk-selected-name">{bulkFile.name}</div>
                    <div className="add-users-bulk-selected-meta">
                      {(bulkFile.size / 1024).toFixed(1)} KB
                      {bulkPreviewData.length > 0 &&
                        ` · ${bulkPreviewData.length} ${bulkPreviewData.length === 1 ? 'row' : 'rows'}`}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="add-users-bulk-selected-remove"
                  onClick={resetBulkState}
                  disabled={bulkUploading}
                  aria-label="Remove file"
                >
                  <X size={16} />
                </button>
              </div>

              {bulkStats && <StatsCards stats={bulkStats} />}
              <ValidationErrors errors={bulkValidationErrors} />
              <DataPreview data={bulkPreviewData} columns={[]} />

              <div className="add-users-bulk-actions">
                <button
                  type="button"
                  className="add-users-bulk-cancel"
                  onClick={resetBulkState}
                  disabled={bulkUploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="add-users-bulk-confirm"
                  onClick={handleBulkConfirm}
                  disabled={bulkUploading}
                >
                  <CheckCircle size={16} />
                  {bulkUploading ? 'Uploading…' : `Confirm & Upload to ${organizationName}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AddUsersToOrganization;
